import { Component, inject, OnInit, ChangeDetectorRef, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { Web3Service } from '../../services/web3.service';
import { ActivityService } from '../../services/activity.service';
import { FARMING_ABI, POSITION_MANAGER_ABI } from '../../config/contracts.config';
import { environment } from '../../../environments/environment';
import { ethers } from 'ethers';

import { RouterModule } from '@angular/router';
import { getNetworkConfigById } from '../../config/network.config';

@Component({
  selector: 'app-yield-farming',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './yield-farming.component.html'
})
export class YieldFarmingComponent implements OnInit {
  public state = inject(AppStateService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private web3Service = inject(Web3Service);
  private activityService = inject(ActivityService);

  public Math = Math;
  public parseFloat = parseFloat;

  public farms: any[] = [];
  public expandedRow: number | null = null;
  public allUserPositions: any[] = [];
  public isLoadingPositions = false;

  // Stake Modal State
  public showNftModal = false;
  public selectedFarmForStake: any = null;
  public availableToStake: any[] = [];
  public isStaking = false;
  public stakingTokenId: number | null = null;

  // Search, Filter, Pagination state
  public searchQuery = '';
  public activeTab = 'Live'; // 'Live' | 'Finished'
  public farmType = 'Farm Types';
  public sortOption = 'Hot';
  public stakedOnly = false;

  public currentPage = 1;
  public itemsPerPage = 10;

  public topStats = {
    tvl: { value: '$12.58B+', change: '+ 8.65%' },
    activeFarms: { value: '128', change: '+ 42.3%' },
    rewardsPaid: { value: '$856M+', change: '+ 15.4%' },
    avgApr: { value: '42.8%', change: '+ 3.21%' }
  };

  public totalRecords = 0;

  constructor() {
    effect(() => {
      const networkId = this.state.selectedNetworkId();
      untracked(() => {
        this.fetchFarms();
        this.fetchStats();
      });
      if (this.state.isWalletConnected()) {
        untracked(() => {
          this.fetchUserPositions();
        });
      } else {
        this.allUserPositions = [];
      }
    });
  }

  ngOnInit() {
    // Moved to constructor effect for reactivity
  }

  fetchStats() {
    this.http.get<any>(`${environment.apiUrl}/user/farms/stats`).subscribe({
      next: (res) => {
        if (res && res.status) {

          const stats = res.data;
          this.topStats.tvl.value = '$' + this.formatNumber(stats.tvl);
          this.topStats.activeFarms.value = stats.activeFarms.toString();
          this.topStats.rewardsPaid.value = '$' + this.formatNumber(stats.rewardsPaid);
          this.topStats.avgApr.value = stats.avgApr.toFixed(2) + '%';
          this.cdr.markForCheck();
        }
      },
      error: (err) => console.error('Failed to load stats', err)
    });
  }

  async syncPositionsWithBlockchain(positions: any[]) {
    try {
      const positionManager = await this.web3Service.getPositionManager();
      const contracts = this.web3Service.getContractsConfig();
      const farmingAddress = contracts.FARMING.toLowerCase();
      const userAddress = this.web3Service.address().toLowerCase();

      const promises = positions.map(async (pos) => {
        try {
          let manager = positionManager;
          if (pos.isExternal && pos.managerAddress) {
            manager = await this.web3Service.getContract(pos.managerAddress, POSITION_MANAGER_ABI);
            console.log(`[DEBUG FarmSync] Position ${pos.tokenId} is external. Using manager: ${pos.managerAddress}`);
          }

          const onchainPos = await manager['positions'](pos.tokenId);
          pos.token0Address = onchainPos.token0.toLowerCase();
          pos.token1Address = onchainPos.token1.toLowerCase();
          pos.onchainFee = Number(onchainPos.fee);

          return pos;
        } catch (e) {
          console.error(`[DEBUG FarmSync] Error fetching onchain positions for TokenID ${pos.tokenId}:`, e);
          return null;
        }
      });

      const results = await Promise.all(promises);
      this.allUserPositions = results.filter(p => p !== null);
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Error syncing positions with blockchain', e);
      this.allUserPositions = positions; // fallback to backend
      this.cdr.markForCheck();
    }
  }

  async fetchUserPositions() {
    if (!this.state.isWalletConnected()) return;
    this.isLoadingPositions = true;
    try {
      const res: any = await firstValueFrom(this.http.get(`${environment.apiUrl}/user/liquidity/user-positions-all`));
      if (res && res.status) {
        await this.syncPositionsWithBlockchain(res.data);
      }
    } catch (err) {
      console.error('Failed to load positions', err);
    } finally {
      this.isLoadingPositions = false;
    }
  }

  fetchFarms() {
    let url = `${environment.apiUrl}/user/farms/list-paginated?page=${this.currentPage}&limit=${this.itemsPerPage}`;

    if (this.searchQuery) {
      url += `&search=${encodeURIComponent(this.searchQuery)}`;
    }

    if (this.sortOption === 'APR') {
      url += `&sort=apr`;
    } else if (this.sortOption === 'Liquidity') {
      url += `&sort=tvl`;
    }

    const statusParam = this.activeTab === 'Live' ? 'active' : 'inactive';
    url += `&status=${statusParam}`;

    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (res && res.status) {

          this.farms = res.data.data.map((f: any) => this.mapFarmData(f));
          this.totalRecords = res.data.count;
          this.cdr.markForCheck();
        }
      },
      error: (err) => console.error('Failed to load farms', err)
    });
  }

  formatNumber(value: number): string {
    if (value === undefined || value === null) return '0.00';
    if (value >= 1000000000000) return (value / 1000000000000).toFixed(2) + 'T';
    if (value >= 1000000000) return (value / 1000000000).toFixed(2) + 'B';
    if (value >= 1000000) return (value / 1000000).toFixed(2) + 'M';
    if (value >= 1000) return (value / 1000).toFixed(2) + 'K';
    return value.toFixed(2);
  }

  mapFarmData(f: any) {
    const pool = f.pool || {};
    const t0 = pool.token0Symbol || 'UNK';
    const t1 = pool.token1Symbol || 'UNK';

    const tags = [];
    if (f.apr > 50) tags.push('High APR');
    if (pool.tvlUsd > 1000000) tags.push('Hot');
    
    const globalTokens = this.state.tokens();
    const t0Token = globalTokens.find(t => t.symbol.toUpperCase() === t0.toUpperCase());
    const t1Token = globalTokens.find(t => t.symbol.toUpperCase() === t1.toUpperCase());

    const feeTierPercent = pool.feeTier ? (pool.feeTier / 10000) + '%' : '0.3%';

    const mapped = {
      id: f.poolAddress,
      pair: `${t0} - ${t1} (${feeTierPercent}) LP`,
      token1: t0.charAt(0),
      token2: t1.charAt(0),
      token0Symbol: t0,
      token1Symbol: t1,
      token0Logo: t0Token?.logoUrl || '',
      token1Logo: t1Token?.logoUrl || '',
      token0Address: pool.token0Address,
      token1Address: pool.token1Address,
      c1: t0Token?.color || '#F3BA2F',
      c2: t1Token?.color || '#D6A848',
      tags: tags,
      earned: 0,
      aprChange: '+0.00%',
      liquidity: '$' + this.formatNumber(pool.tvlUsd || 0),
      multiplier: (f.allocPoint || 0) + 'x',
      available: '0 LP',
      staked: '0 LP',
      feeTier: pool.feeTier ? (pool.feeTier / 10000) + '%' : '0.3%',
      apr: f.apr ? f.apr.toFixed(2) + '%' : '0.00%',
      rawApr: f.apr || 0,
      tvl: pool.tvlUsd ? '$' + this.formatNumber(pool.tvlUsd) : '$0.00',
      totalStaked: '$' + this.formatNumber((pool.tvlUsd || 0) * 0.4),
      poolAddress: f.poolAddress
    };

    return mapped;
  }

  getBlockExplorerUrl(address: string): string {
    const config = getNetworkConfigById(this.state.selectedNetworkId());
    const explorer = config ? config.blockExplorerUrls[0] : 'https://etherscan.io';
    return `${explorer}/address/${address}`;
  }

  getFarmPositions(farm: any) {
    const farmT0 = farm.token0Address?.toLowerCase();
    const farmT1 = farm.token1Address?.toLowerCase();
    
    let parsedFarmFee = farm.feeTier;
    if (typeof farm.feeTier === 'string' && farm.feeTier.includes('%')) {
       parsedFarmFee = Math.round(parseFloat(farm.feeTier) * 10000);
    } else {
       parsedFarmFee = Number(farm.feeTier || 3000);
    }

    const positions = this.allUserPositions.filter(p => {
      // 1. Direct pool address match (for internal pools where backend has it)
      if (p.poolAddress && p.poolAddress !== '0x0000000000000000000000000000000000000000') {
        if (p.poolAddress.toLowerCase() === farm.id.toLowerCase()) return true;
      }
      
      // 2. On-chain token match (for external pools)
      if (p.token0Address && p.token1Address) {
        const onchainT0 = p.token0Address.toLowerCase();
        const onchainT1 = p.token1Address.toLowerCase();
        const tokensMatch = (onchainT0 === farmT0 && onchainT1 === farmT1) || 
                            (onchainT0 === farmT1 && onchainT1 === farmT0);
        
        // Match tokens AND fee tier
        if (tokensMatch && p.onchainFee === parsedFarmFee) {
          return true;
        }
      }

      // 3. Fallback to symbol match
      return p.token0Symbol && p.token1Symbol && p.token0Symbol === farm.token0Symbol && p.token1Symbol === farm.token1Symbol;
    });
    const result = {
      all: positions,
      hasPositions: positions.length > 0,
      staked: positions.filter(p => p.isStakedInFarm),
      available: positions.filter(p => !p.isStakedInFarm)
    };
    
    console.log(`[DEBUG getFarmPositions] Farm ${farm.id}:`, {
      farmToken0: farm.token0Symbol, farmToken1: farm.token1Symbol,
      matchedPositions: positions,
      result: result
    });
    
    if (result.hasPositions) {

    }
    return result;
  }

  onFilterChange() {
    this.currentPage = 1;
    this.expandedRow = null;
    this.fetchFarms();
  }

  toggleRow(index: number) {
    if (this.expandedRow === index) {
      this.expandedRow = null;
    } else {
      this.expandedRow = index;
    }
  }

  isFetchingModal: string | null = null;

  async openStakeModal(farm: any) {
    if (!this.web3Service.isConnected()) {
      this.state.triggerNotification("Please connect your wallet first.", false);
      return;
    }
    
    try {
      this.isFetchingModal = farm.id;

      // 1. Fetch latest from backend
      const res: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/liquidity/user-positions-all`),
      );
      let dbPositions = res?.data || [];
      console.log('DEBUG: dbPositions fetched from backend API:', dbPositions);
      console.log('DEBUG: Is NFT #7 in dbPositions?', dbPositions.some((p: any) => Number(p.tokenId) === 7));
      
      const positionManager = await this.web3Service.getPositionManager();
      const targetFarmPositions: any[] = [];

      for (const p of dbPositions) {
        try {
          let manager = positionManager;
          if (p.isExternal && p.managerAddress) {
            manager = await this.web3Service.getContract(p.managerAddress, POSITION_MANAGER_ABI);
          }
          
          const onchainPos = await manager['positions'](p.tokenId);
          
          let parsedFarmFee = farm.feeTier;
          if (typeof farm.feeTier === 'string' && farm.feeTier.includes('%')) {
             parsedFarmFee = Math.round(parseFloat(farm.feeTier) * 10000);
          }

          const onchainT0 = onchainPos.token0.toLowerCase();
          const onchainT1 = onchainPos.token1.toLowerCase();
          const farmT0 = farm.token0Address?.toLowerCase();
          const farmT1 = farm.token1Address?.toLowerCase();

          const tokensMatch = (onchainT0 === farmT0 && onchainT1 === farmT1) || 
                              (onchainT0 === farmT1 && onchainT1 === farmT0);

          if (
            farm.token0Address && farm.token1Address &&
            tokensMatch &&
            Number(onchainPos.fee) === Number(parsedFarmFee)
          ) {
            targetFarmPositions.push(p);
          }
        } catch (e) {
          console.warn(`Could not fetch on-chain position for token ${p.tokenId}`);
        }
      }
      console.log('Farm ID:', farm.id);
      console.log('All DB Positions:', dbPositions);
      console.log('DEBUG: Target Farm Positions (matching token0/token1/fee):', targetFarmPositions);

      // 3. Only verify blockchain ownership for THESE specific NFTs (prevents RPC rate-limiting)
      const contracts = this.web3Service.getContractsConfig();
      const farmingAddress = contracts.FARMING.toLowerCase();
      const userAddress = this.web3Service.address().toLowerCase();

      const promises = targetFarmPositions.map(async (pos: any) => {
        try {
          let manager = positionManager;
          if (pos.isExternal && pos.managerAddress) {
            manager = await this.web3Service.getContract(pos.managerAddress, POSITION_MANAGER_ABI);
          }
          // Trust database for staking status
          return pos;
        } catch (e) {
          console.error(`[DEBUG openStakeModal] Error checking onchain position for tokenId ${pos.tokenId}:`, e);
          return null; // burned or doesn't exist
        }
      });

      const validFarmPositions = (await Promise.all(promises)).filter((p) => p !== null);

      this.availableToStake = validFarmPositions.filter((p: any) => !p.isStakedInFarm);
      this.selectedFarmForStake = farm;
      this.showNftModal = true;
      console.log('availableToStake', this.availableToStake);
      this.syncPositionsWithBlockchain(dbPositions).catch(console.error);
    } catch (e) {
      console.error("Error fetching latest positions", e);
    } finally {
      this.isFetchingModal = null;
    }
  }

  closeStakeModal() {
    if (this.isStaking) {
      this.state.triggerNotification("Please wait for the transaction to complete.", false);
      return;
    }
    this.showNftModal = false;
    this.selectedFarmForStake = null;
    this.availableToStake = [];
  }

  async stakeSelectedToken(tokenId: number) {
    if (this.isStaking) return;
    const farm = this.selectedFarmForStake;
    if (!farm) return;

    this.isStaking = true;
    this.stakingTokenId = tokenId;

    try {
      const contracts = this.web3Service.getContractsConfig();
      const farmingContract = await this.web3Service.getContract(contracts.FARMING, FARMING_ABI);
      console.log('FARMING CONTRACT-------------', farm.id);
      console.log('FARMING CONTRACT-------------', contracts.FARMING);
      const positionManager = await this.web3Service.getPositionManager();

      const owner = await positionManager['ownerOf'](tokenId);
      if (owner.toLowerCase() !== this.web3Service.address().toLowerCase()) {
        this.state.triggerNotification("You do not own this Token ID", false);
        this.isStaking = false;
        return;
      }

      this.state.triggerNotification("Approving NFT for farming...", true);
      const approveTx = await positionManager['approve'](contracts.FARMING, tokenId);
      await approveTx.wait();

      const poolLength = await farmingContract['poolLength']();
      console.log('POOOL LENGTH', poolLength);
      let pid = -1;
      for (let i = 0; i < Number(poolLength); i++) {
        const poolInfo = await farmingContract['poolInfo'](i);
        console.log('pool info', poolInfo, '\n farmId', farm.id);
        if (poolInfo.targetPool.toLowerCase() === farm.id.toLowerCase()) {
          pid = i;
          break;
        }
      }

      if (pid === -1) {
        this.state.triggerNotification("Farming Pool not found on the smart contract.", false);
        this.isStaking = false;
        return;
      }

      const tx = await farmingContract['stake'](pid, tokenId);
      await tx.wait();

      this.state.triggerNotification("Successfully staked LP NFT!", true);
      this.isStaking = false;
      this.stakingTokenId = null;
      this.closeStakeModal();
      
      const pos = this.allUserPositions.find((p: any) => p.tokenId === tokenId);
      if (pos) {
        pos.isStakedInFarm = true;
        pos.farmAddress = farm.id.toLowerCase();
      }
      
      try {
        await firstValueFrom(this.http.post(
          `${environment.apiUrl}/user/liquidity/user-positions/sync`,
          { tokenId: tokenId, action: 'stake', poolAddress: farm.id, networkId: this.state.selectedNetworkId() },
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
        ));

        // Unified Activity Log
        await firstValueFrom(this.activityService.recordActivity({
          txHash: tx.hash,
          chainId: this.state.selectedNetworkId(),
          actionType: 'FARM_STAKE',
          status: 'Success',
          payload: {
            tokenId: tokenId,
            poolAddress: farm.id
          },
          platformName: 'DEX Farming'
        }));
      } catch (err) {
        console.error('Backend sync failed after staking', err);
      }
      
      setTimeout(() => {
        this.fetchUserPositions();
      }, 3000);
    } catch (err: any) {
      console.error(err);
      this.state.triggerNotification(this.state.handleError(err, "Staking failed"), false);
    }

    this.isStaking = false;
    this.stakingTokenId = null;
    this.cdr.markForCheck();
  }

  async unstakeLP(farm: any) {
    if (!this.web3Service.isConnected()) {
      this.state.triggerNotification("Please connect your wallet first.", false);
      return;
    }

    const farmPositions = this.getFarmPositions(farm);
    const stakedTokens = farmPositions.staked;
    if (stakedTokens.length === 0) {
      this.state.triggerNotification("No staked tokens found for this farm.", false);
      return;
    }

    const tokenId = stakedTokens[0].tokenId;

    this.isStaking = true;
    this.stakingTokenId = tokenId;
    try {
      const contracts = this.web3Service.getContractsConfig();
      const farmingContract = await this.web3Service.getContract(contracts.FARMING, FARMING_ABI);

      const poolLength = await farmingContract['poolLength']();
      let pid = -1;
      for (let i = 0; i < Number(poolLength); i++) {
        const poolInfo = await farmingContract['poolInfo'](i);
        if (poolInfo.targetPool.toLowerCase() === farm.id.toLowerCase()) {
          pid = i;
          break;
        }
      }

      if (pid === -1) {
        this.state.triggerNotification("Farming Pool not found on the smart contract.", false);
        this.isStaking = false;
        return;
      }

      this.state.triggerNotification("Unstaking NFT...", true);
      
      const pendingRewardBN = await farmingContract['pendingReward'](pid, tokenId);
      const claimedRewardAmount = ethers.formatUnits(pendingRewardBN, 18).toString();

      const tx = await farmingContract['unstake'](pid, tokenId);
      await tx.wait();

      this.state.triggerNotification("Successfully unstaked LP NFT!", true);
      
      // Optimistic update
      const pos = this.allUserPositions.find((p: any) => p.tokenId === tokenId);
      if (pos) {
        pos.isStakedInFarm = false;
        pos.farmAddress = null;
      }
      
      try {
        await firstValueFrom(this.http.post(
          `${environment.apiUrl}/user/liquidity/user-positions/sync`,
          { tokenId: tokenId, action: 'unstake', networkId: this.state.selectedNetworkId() },
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
        ));

        // Unified Activity Log
        await firstValueFrom(this.activityService.recordActivity({
          txHash: tx.hash,
          chainId: this.state.selectedNetworkId(),
          actionType: 'FARM_UNSTAKE',
          status: 'Success',
          payload: {
            tokenId: tokenId,
            poolAddress: farm.id
          },
          platformName: 'DEX Farming'
        }));

        if (Number(claimedRewardAmount) > 0) {
          const chainId = this.state.selectedNetworkId();
          const contracts = environment.contracts as any;
          const dxorToken = contracts[chainId]?.TOKENS?.DXOR || '';

          await firstValueFrom(this.activityService.recordActivity({
            txHash: tx.hash,
            chainId: chainId,
            actionType: 'FARM_CLAIM',
            status: 'Success',
            payload: {
              farmPid: pid,
              tokenId: tokenId,
              poolAddress: farm.id,
              rewardAmount: ethers.parseUnits(Number(claimedRewardAmount).toFixed(6), 18).toString(),
              tokenAddress: dxorToken
            },
            platformName: 'DEX Farming'
          }));
        }
      } catch (err) {
        console.error('Backend sync failed after unstaking', err);
      }
      
      setTimeout(() => {
        this.fetchUserPositions();
      }, 3000);
    } catch (err: any) {
      console.error(err);
      this.state.triggerNotification(this.state.handleError(err, "Unstaking failed"), false);
    }

    this.isStaking = false;
    this.stakingTokenId = null;
  }

  get totalPages() {
    return Math.ceil(this.totalRecords / this.itemsPerPage) || 1;
  }

  get paginatedFarms() {
    if (this.stakedOnly) {

      return this.farms.filter(f => this.getFarmPositions(f).staked.length > 0);
    }
    return this.farms;
  }

  get paginationPages() {
    const pages = [];
    const total = this.totalPages;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (this.currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (this.currentPage >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...', total);
      }
    }
    return pages;
  }

  onSearchChange() {
    this.currentPage = 1;
    this.expandedRow = null;
    this.fetchFarms();
  }

  goToPage(page: number | string) {
    if (typeof page === 'number') {
      this.currentPage = page;
      this.expandedRow = null;
      this.fetchFarms();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.expandedRow = null;
      this.fetchFarms();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.expandedRow = null;
      this.fetchFarms();
    }
  }
}
